import React, { useEffect, useState } from 'react';
import { EventType } from '@azure/msal-browser';
import type { AuthenticationResult, EventMessage } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { msalInstance } from './msalInstance';
import { isAuthEnabled } from './authEnabled';

interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * Wraps the application with the MSAL authentication provider.
 *
 * When {@link isAuthEnabled} is false (no ENTRA_CLIENT_ID configured),
 * the provider is skipped and children are rendered directly — this
 * allows local development without any Entra ID configuration.
 */
export default function AuthProvider({ children }: AuthProviderProps) {
  const [isInitialized, setIsInitialized] = useState(!isAuthEnabled);

  useEffect(() => {
    if (!isAuthEnabled) return;

    const init = async () => {
      // 1. Initialize the MSAL instance (required before any other call in v3+)
      await msalInstance.initialize();

      // 2. Register the event callback BEFORE handling redirects so we don't
      //    miss the LOGIN_SUCCESS event that fires during handleRedirectPromise.
      msalInstance.addEventCallback((event: EventMessage) => {
        if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
          const result = event.payload as AuthenticationResult;
          msalInstance.setActiveAccount(result.account);
        }
      });

      // 3. Handle the redirect response (returns the auth result if this is
      //    a redirect back from Entra ID, or null otherwise).
      const response = await msalInstance.handleRedirectPromise();
      if (response?.account) {
        msalInstance.setActiveAccount(response.account);
      }

      // 4. If no active account was set by the redirect, pick the first
      //    cached account (user may already be signed in from a prior session).
      if (!msalInstance.getActiveAccount()) {
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          msalInstance.setActiveAccount(accounts[0]);
        }
      }

      setIsInitialized(true);
    };

    init();
  }, []);

  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-500">Initializing authentication...</p>
      </div>
    );
  }

  if (!isAuthEnabled) {
    return <>{children}</>;
  }

  return <MsalProvider instance={msalInstance}>{children}</MsalProvider>;
}
